import React, { FC, useEffect, useState } from 'react';
import {
    ActivityIndicator,
    Image,
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TouchableOpacity,
    useWindowDimensions,
    View,
} from 'react-native';
import { ThemeConsumer } from '@rneui/themed';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Popover from 'react-native-popover-view';

import { appColors, appFonts, fontSize } from '../../../themes/appTheme';
import {
    ZenBadge,
    ZenButton,
    ZenCheckBox,
    ZenDivider,
    ZenIcon,
    ZenInput,
    ZenText,
    ZenToast,
} from 'zen-core-elements';

import { useAppDispatch } from '../../../redux/hooks';
import { config } from '../../../shared/environment';
import {
    addPosInventoryQuantity,
    getPosPreOrderQuantity,
    setPosInventoryQuantity,
} from '../../../redux/slices/inventorySlice';
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox';
import {
    InventoryQuantityPayload,
    UpdateQtyFormik,
    UpdateQtyProps,
} from './inventory.model';

const UpdateQty: FC<UpdateQtyProps> = ({
    item,
    load,
    selectedLocation,
    setIsUpdatingQuantity,
    setLoad,
    getAllInventoryProducts,
}) => {
    const dispatch = useAppDispatch();
    const { width } = useWindowDimensions();

    const [leavePopover, setLeavePopover] = useState<boolean>(false);
    const [pressedButton, setPressedButton] = useState<'set' | 'add'>();
    const [preOrderStock, setPreOrderStock] = useState<number | null>(null);
    const [imageError, setImageError] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Fetch pre-order stock needed for this product/location
    useEffect(() => {
        let isMounted = true;
        const fetchPreOrderQuantity = async () => {
            const payload = {
                storeId: config?.storeId,
                data: {
                    productId: item?.product?.id,
                    variantId: item?.id,
                    locationId: selectedLocation,
                },
            };
            try {
                const res = await dispatch(getPosPreOrderQuantity(payload)).unwrap();
                if (isMounted && res) {
                    setPreOrderStock(res?.preOrderStockNeeded ?? null);
                }
            } catch (err) {
                console.log('Error fetching pre-order stock:', err);
            }
        };

        if (item?.product?.id && item?.id) {
            fetchPreOrderQuantity();
        }
        return () => {
            isMounted = false;
        };
    }, [item, selectedLocation, dispatch]);

    // Handle leave confirmation popover
    const confirmation = (confirmItem: { id: number }) => {
        if (confirmItem?.id === 2) {
            setLeavePopover(false);
            setIsUpdatingQuantity(false);
        } else {
            setLeavePopover(false);
        }
    };

    const validationSchema = Yup.object().shape({
        quantity: Yup.number()
            .typeError('Enter a valid whole number')
            .label('Quantity')
            .min(0, 'Quantity must be 0 or greater')
            .max(100000, 'Quantity cannot exceed 100,000')
            .integer('Quantity accepts only whole numbers'),
    });

    const rawStock = item?.multiLocationVariantStocks?.[0]?.stock;
    const currentStock =
        rawStock != null && !isNaN(Number(rawStock))
            ? Number(rawStock)
            : null;

    const formik = useFormik<UpdateQtyFormik>({
        initialValues: {
            quantity:
                currentStock !== null &&
                    currentStock !== -1 &&
                    currentStock !== 0
                    ? String(currentStock)
                    : '',
            isUnlimited: false,
            isUnstock: false,
        },
        onSubmit: () => handleInventoryUpdate(),
        enableReinitialize: true,
        validationSchema,
    });

    const handleInventoryUpdate = async () => {
        setSubmitting(true);
        const enteredQty = Number(formik?.values?.quantity);

        const productName = item?.product?.name
            ? item.product.name.concat(
                item?.product?.isVariants && item?.variantName
                    ? ' - ' + item.variantName
                    : ''
            )
            : 'Product';

        const stockPayload =
            item?.multiLocationVariantStocks && item.multiLocationVariantStocks[0]?.stock
                ? item.multiLocationVariantStocks[0].stock === -1
                    ? 'Infinity'
                    : item.multiLocationVariantStocks[0].stock
                : 0;

        const payload: InventoryQuantityPayload = {
            storeId: config?.storeId,
            data: {
                product_id: item?.product?.id,
                productName,
                stock: stockPayload,
                ...(pressedButton === 'add'
                    ? { addedQuantity: enteredQty }
                    : {
                        setQuantity:
                            formik?.values?.quantity === '' ? -1 : enteredQty,
                    }),
                isVariant: Boolean(item?.product?.isVariants),
                variantDetails: {
                    id: item?.id,
                    name: item?.variantName || '',
                    quantityForm: enteredQty >= 0 ? String(enteredQty) : '',
                    stock:
                        item?.multiLocationVariantStocks && item.multiLocationVariantStocks[0]
                            ? item.multiLocationVariantStocks[0].stock
                            : 0,
                    variantSkuId: item?.id,
                },
                locationId: selectedLocation,
            },
        };

        try {
            if (pressedButton === 'add') {
                const res = await dispatch(addPosInventoryQuantity(payload)).unwrap();
                if (res) {
                    await getAllInventoryProducts();
                    ZenToast({ type: 'success', message: res?.message || 'Stock quantity added successfully' });
                    setIsUpdatingQuantity(false);
                }
            } else {
                const res = await dispatch(setPosInventoryQuantity(payload)).unwrap();
                if (res) {
                    await getAllInventoryProducts();
                    ZenToast({ type: 'success', message: res?.message || 'Stock quantity updated successfully' });
                    setIsUpdatingQuantity(false);
                }
            }
        } catch (err: any) {
            console.log('Error updating stock quantity:', err);
            ZenToast({
                type: 'error',
                message: err?.response?.data?.message || err?.message || 'Failed to update stock quantity',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const enteredQtyStr = formik.values.quantity ? formik.values.quantity.trim() : '';
    const initialQtyStr = formik.initialValues.quantity ? formik.initialValues.quantity.trim() : '';

    const enteredQtyNum = enteredQtyStr !== '' ? Number(enteredQtyStr) : null;
    const initialQtyNum = initialQtyStr !== '' ? Number(initialQtyStr) : null;

    const isValidWholeNumber =
        enteredQtyStr !== '' &&
        !isNaN(Number(enteredQtyStr)) &&
        Number.isInteger(Number(enteredQtyStr)) &&
        Number(enteredQtyStr) >= 0 &&
        Number(enteredQtyStr) <= 100000 &&
        !formik.errors.quantity;

    const isQuantityChangedManually =
        isValidWholeNumber &&
        enteredQtyNum !== initialQtyNum &&
        !formik.values.isUnstock &&
        !formik.values.isUnlimited;

    const isSetButtonEnabled =
        formik.values.isUnstock !== formik.initialValues.isUnstock ||
        formik.values.isUnlimited !== formik.initialValues.isUnlimited ||
        isQuantityChangedManually;

    // Stock Badge formatting
    let currentStockText = 'Not Available';
    let currentStockBadgeBg = '#F1F5F9';
    let currentStockBadgeColor = '#64748B';

    if (currentStock !== null && currentStock !== undefined) {
        if (currentStock === -1) {
            currentStockText = 'Unlimited';
            currentStockBadgeBg = '#E0F2FE';
            currentStockBadgeColor = appColors.primary;
        } else if (currentStock === 0) {
            currentStockText = '0 (Out of stock)';
            currentStockBadgeBg = '#FEEAEA';
            currentStockBadgeColor = appColors.red;
        } else {
            currentStockText = `${currentStock} in stock`;
            currentStockBadgeBg = '#E8F8F0';
            currentStockBadgeColor = appColors.activeColor;
        }
    }

    const imageUri =
        !imageError && item?.product?.featured_image
            ? config.AWSImageUrl + item.product.featured_image
            : null;

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    style={styles.keyboardContainer}
                >
                    <View style={styles.modalBackdrop}>
                        <View
                            style={[
                                styles.modalCard,
                                {
                                    width: width > 700 ? 540 : width * 0.92,
                                },
                            ]}
                        >
                            {/* Modal Header */}
                            <View style={styles.header}>
                                <View style={styles.headerLeft}>
                                    <View style={styles.headerIconCircle}>
                                        <ZenIcon
                                            name="cube-outline"
                                            type="material-community"
                                            size={20}
                                            color={theme?.primary ?? appColors.primary}
                                        />
                                    </View>
                                    <View>
                                        <ZenText
                                            content="Update Quantity"
                                            bold
                                            size={fontSize.largerFontSize}
                                            color={theme?.dark ?? appColors.dark}
                                        />
                                        <ZenText
                                            content="Adjust stock levels for this product"
                                            size={fontSize.tinyFontSize}
                                            color={theme?.labelColor ?? appColors.labelColor}
                                        />
                                    </View>
                                </View>
                                <TouchableOpacity
                                    onPress={() => {
                                        if (formik?.dirty) {
                                            setLeavePopover(true);
                                        } else {
                                            setIsUpdatingQuantity(false);
                                        }
                                    }}
                                    style={styles.closeButton}
                                    accessibilityLabel="Close update modal"
                                >
                                    <ZenIcon
                                        name="close"
                                        type="material-community"
                                        size={20}
                                        color={appColors.iconColor}
                                    />
                                </TouchableOpacity>
                            </View>

                            <View style={styles.headerDivider} />

                            {/* Product Summary Panel */}
                            <View style={styles.productBanner}>
                                <Image
                                    source={imageUri ? { uri: imageUri } : require('../../../assets/noimage.jpg')}
                                    style={styles.productImage}
                                    resizeMode="cover"
                                    onError={() => setImageError(true)}
                                />
                                <View style={styles.productDetails}>
                                    <ZenText
                                        bold
                                        numberOfLines={1}
                                        size={fontSize.mediumFontSize}
                                        content={item?.product?.name || 'Product'}
                                        color={theme?.dark ?? appColors.dark}
                                    />
                                    {item?.variantName && !item?.isDefault && (
                                        <ZenText
                                            numberOfLines={1}
                                            size={fontSize.smallerFontSize}
                                            content={`Variant: ${item.variantName}`}
                                            color={theme?.labelColor ?? appColors.labelColor}
                                        />
                                    )}
                                    <View style={styles.badgeRow}>
                                        <ZenBadge
                                            badgeColor={currentStockBadgeBg}
                                            badgeName={currentStockText}
                                            badgeTextColor={currentStockBadgeColor}
                                            badgeContainerStyle={styles.stockBadge}
                                            badgeTextStyle={styles.badgeText}
                                        />
                                        {preOrderStock !== null && preOrderStock > 0 && (
                                            <ZenBadge
                                                badgeColor="#FEF3C7"
                                                badgeName={`Pre-order needed: ${preOrderStock}`}
                                                badgeTextColor="#B45309"
                                                badgeContainerStyle={styles.stockBadge}
                                                badgeTextStyle={styles.badgeText}
                                            />
                                        )}
                                    </View>
                                </View>
                            </View>

                            {/* Input Form Section */}
                            <View style={styles.formContainer}>
                                <ZenInput
                                    label="Quantity *"
                                    keyboardType="numeric"
                                    onBlur={() => formik?.handleBlur('quantity')}
                                    errorMessage={
                                        formik?.errors?.quantity &&
                                            !formik?.isValid &&
                                            formik?.touched?.quantity
                                            ? formik?.errors?.quantity?.toString()
                                            : ''
                                    }
                                    value={
                                        formik.values.quantity === '-1' ||
                                            (formik?.values?.quantity === '0' &&
                                                !formik?.values?.isUnstock)
                                            ? ''
                                            : formik?.values?.quantity
                                    }
                                    onChangeText={(text) => {
                                        const cleanText = text.replace(/[^0-9]/g, '');
                                        formik.setFieldValue('quantity', cleanText);
                                        if (Number(cleanText) > 0) {
                                            formik?.setFieldValue('isUnstock', false);
                                            formik?.setFieldValue('isUnlimited', false);
                                        } else if (
                                            cleanText === '0' &&
                                            currentStock !== 0
                                        ) {
                                            formik?.setFieldValue('isUnstock', true);
                                            formik?.setFieldValue('isUnlimited', false);
                                        } else if (cleanText === '') {
                                            formik?.setFieldValue('isUnstock', false);
                                            formik?.setFieldValue('isUnlimited', false);
                                        }
                                    }}
                                    placeholder="Enter quantity"
                                    type="outlined"
                                    labelStyle={styles.inputLabel}
                                    errorStyle={styles.errorStyle}
                                    inputStyle={styles.inputStyle}
                                    inputContainerStyle={styles.inputContainer}
                                />

                                {/* Unstock Checkbox Toggle */}
                                <View style={styles.checkboxWrapper}>
                                    <ZenCheckBox
                                        checked={formik?.values?.isUnstock}
                                        checkedColor={theme?.primary ?? appColors.primary}
                                        containerStyle={styles.checkboxContainer}
                                        disabled={currentStock === 0}
                                        title="Mark as Unstocked (0 quantity)"
                                        titleStyle={styles.checkboxTitle}
                                        onPress={() => {
                                            formik.handleBlur('isUnstock');
                                            formik?.setFieldValue('isUnlimited', false);
                                            formik?.setFieldValue(
                                                'isUnstock',
                                                !formik?.values?.isUnstock
                                            );
                                            formik?.setFieldValue(
                                                'quantity',
                                                !formik?.values?.isUnstock ? '0' : ''
                                            );
                                        }}
                                    />
                                </View>
                            </View>

                            <ZenDivider style={styles.footerDivider} />

                            {/* Modal Footer Actions */}
                            <View style={styles.footer}>


                                <View style={styles.actionButtonsRow}>
                                    {/* Add Button: Adds to existing stock */}
                                    <ZenButton
                                        size="sm"
                                        roundedSize="md"
                                        title={submitting && pressedButton === 'add' ? 'Adding...' : 'Add (+)'}
                                        disabled={!isQuantityChangedManually || submitting}
                                        type="outline"
                                        outlineColor={theme?.primary ?? appColors.primary}
                                        buttonStyle={styles.addButton}
                                        titleStyle={{
                                            color:
                                                !isQuantityChangedManually || submitting
                                                    ? appColors.grey
                                                    : theme?.primary ?? appColors.primary,
                                            fontFamily: appFonts.bold,
                                            fontSize: fontSize.smallFontSize,
                                        }}
                                        onPress={() => {
                                            setPressedButton('add');
                                            formik?.handleSubmit();
                                        }}
                                    />

                                    {/* Set Button: Sets the exact stock value */}
                                    <ZenButton
                                        size="sm"
                                        roundedSize="md"
                                        title={submitting && pressedButton === 'set' ? 'Saving...' : 'Set Stock'}
                                        disabled={!isSetButtonEnabled || submitting}
                                        buttonStyle={[
                                            styles.setButton,
                                            {
                                                backgroundColor:
                                                    !isSetButtonEnabled || submitting
                                                        ? '#CBD5E1'
                                                        : theme?.primary ?? appColors.primary,
                                            },
                                        ]}
                                        titleStyle={{
                                            color: appColors.light,
                                            fontFamily: appFonts.bold,
                                            fontSize: fontSize.smallFontSize,
                                        }}
                                        onPress={() => {
                                            setPressedButton('set');
                                            formik?.handleSubmit();
                                        }}
                                    />
                                </View>
                            </View>
                        </View>

                        {/* Leave Confirmation Popover */}
                        <Popover
                            isVisible={leavePopover}
                            popoverStyle={{
                                width: width > 900 ? 500 : 420,
                                borderRadius: 14,
                            }}
                        >
                            <CommonDialogBox
                                type="EDIT"
                                replaceValue="page"
                                confirmation={confirmation}
                            />
                        </Popover>
                    </View>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
};

export default UpdateQty;

const styles = StyleSheet.create({
    keyboardContainer: {
        flex: 1,
    },
    modalBackdrop: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'transparent',
    },
    modalCard: {
        backgroundColor: appColors.light,
        borderRadius: 20,
        maxWidth: 560,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: 18,
        paddingBottom: 14,
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 12,
    },
    headerIconCircle: {
        width: 38,
        height: 38,
        borderRadius: 19,
        backgroundColor: '#F0F7FA',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(0, 83, 116, 0.1)',
    },
    closeButton: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: '#F1F5F9',
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerDivider: {
        height: 1,
        backgroundColor: '#F1F5F9',
    },
    productBanner: {
        marginHorizontal: 20,
        marginTop: 16,
        padding: 12,
        backgroundColor: '#F8FAFC',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 14,
    },
    productImage: {
        width: 64,
        height: 64,
        borderRadius: 10,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
    },
    productDetails: {
        flex: 1,
        rowGap: 4,
    },
    badgeRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 2,
    },
    stockBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    badgeText: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.badgeFontSize,
    },
    formContainer: {
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8,
    },
    inputLabel: {
        color: appColors.labelColor,
        fontSize: fontSize.smallerFontSize,
        fontFamily: appFonts.medium,
        marginBottom: 4,
    },
    inputContainer: {
        borderRadius: 10,
        borderColor: '#CBD5E1',
    },
    inputStyle: {
        fontSize: fontSize.mediumFontSize,
        color: appColors.dark,
        fontFamily: appFonts.medium,
    },
    errorStyle: {
        fontSize: fontSize.errorFontSize,
        fontFamily: appFonts.medium,
        color: appColors.red,
        paddingTop: 4,
    },
    checkboxWrapper: {
        marginTop: 6,
    },
    checkboxContainer: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        marginLeft: 0,
    },
    checkboxTitle: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.smallFontSize,
        color: appColors.lightDark,
        fontWeight: 'normal',
    },
    footerDivider: {
        marginTop: 12,
        backgroundColor: '#F1F5F9',
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#FAFAFA',
    },
    cancelButton: {
        borderRadius: 7,
        paddingHorizontal: 14,
        paddingVertical: 0,
    },
    actionButtonsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 8,
    },
    addButton: {
        borderRadius: 7,
        paddingHorizontal: 14,
        paddingVertical: 0,
    },
    setButton: {
        borderRadius: 7,
        paddingHorizontal: 16,
        paddingVertical: 0,
    },
});